import React, { useState } from 'react';
import { Box } from '@mui/material';
import amplitudeLog from '@client/amplitude';
import { AMP_APP_OPEN_RECIPE_SELECTOR } from '@shared/constants';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  canChangeElementCategory,
  getElementCategory,
  isSystemCategoryElement,
} from '@shared/helpers/element-category.helpers';
import { IElement } from '@shared/models/project.model';
import { useSelectElementCategory } from '@client/hooks/use-element-category.hooks';
import ElementPropertyPanel from './ElementPropertyPanel';
import { useRecipeContextElement } from '@client/hooks/use-recipe-context-element.hooks';
import {
  useRecipe,
  useRecipeIsModified,
} from '@features/recipes/use-recipes.hooks';
import RecipeSelector from '@features/recipes/components/RecipeSelector';
import { useIsReadonlyElement } from '@client/hooks/use-user.hooks';
import RecipeActionMenu from '@features/recipes/components/RecipeActionMenu';
import { SidePanel } from '@ui/layout';
import { HeaderTextBox } from './HeaderTextBox';
import { RecipeSelectorButton } from './RecipeSelectorButton';
import { ElementCategoryMenuIconButton } from '@features/elements/components/ElementCategoryMenu/ElementCategoryMenuIconButton';
import { useGroupedElementCategories } from '@features/elements/hooks/use-grouped-element-categories.hook';
import { ElementCoreSettingsProperties } from './ElementCoreSettingsProperties';

interface ElementCategorySettingsProps {
  element: IElement;
  includeCategories?: Readonly<ElementCategoryID[]>;
}

export const ElementCoreSettings: React.FC<ElementCategorySettingsProps> = ({
  element,
  includeCategories = [],
}) => {
  const recipeElement = useRecipeContextElement(element) ?? element;
  const selectElementCategory = useSelectElementCategory(recipeElement);
  const recipe = useRecipe(recipeElement);
  const recipeIsModified = useRecipeIsModified(recipeElement);
  const readonly = useIsReadonlyElement(element);
  const categoryLocked = !canChangeElementCategory(element);

  const categoryGroups = useGroupedElementCategories(
    recipeElement,
    includeCategories,
  );

  const [isRecipeSelectorOpen, setIsRecipeSelectorOpen] = useState(false);

  const handleCategorySelect = async (categoryId: ElementCategoryID) => {
    const selected = await selectElementCategory(categoryId);
    if (selected === 'cancelled') return; // nothing was selected
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <ElementCategoryMenuIconButton
            selectedCategoryId={
              recipeElement.category_id || ElementCategoryID.None
            }
            onSelect={handleCategorySelect}
            disabled={readonly}
            categoryLocked={categoryLocked}
            categoryGroups={categoryGroups}
          />
          {isSystemCategoryElement(recipeElement) ? (
            <RecipeSelectorButton
              onClick={() => {
                amplitudeLog(AMP_APP_OPEN_RECIPE_SELECTOR);
                setIsRecipeSelectorOpen(true);
              }}
              recipe={recipe}
              isRecipeDefined={recipe !== undefined}
              isEdited={
                recipeIsModified.status === 'ready' &&
                recipeIsModified.isModified
              }
              disabled={readonly}
            />
          ) : (
            <HeaderTextBox>{getElementCategory(element)?.name}</HeaderTextBox>
          )}
        </Box>
        {isSystemCategoryElement(recipeElement) && (
          <RecipeActionMenu element={recipeElement} recipe={recipe} />
        )}
      </Box>
      {isSystemCategoryElement(recipeElement) && (
        <ElementPropertyPanel element={recipeElement} />
      )}
      <SidePanel.Content.Divider />
      <ElementCoreSettingsProperties element={element} />
      {isRecipeSelectorOpen && (
        <RecipeSelector
          onClose={() => setIsRecipeSelectorOpen(false)}
          element={recipeElement}
        />
      )}
    </Box>
  );
};
